import { useEffect, useState, useRef } from "react";
import useGetJobs from "../hooks/useGetJobs";
import AddButton from "../../../../components/AddButton";
import EditButton from "../../../../components/EditButton";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { useRecoilState, useRecoilValue } from "recoil";
import { BriefcaseIcon } from "@heroicons/react/24/solid";
import TableFilters from "../TableFilters";
import { useLocation } from "react-router-dom";
import { notificationFilterById } from "../../../../actions/Utilities/NotificationFilter";
import JobForm from "../forms/JobForm";
import {
  jobShowState,
  addShowState,
  editState,
  rowForEdit,
  selectedRowsJobToDelete,
} from "../atoms/atoms";
import JobViewDetails from "./JobViewDetails";
import MaterialTableTS from "../../../../components/tables/MaterialTableTS";
import useDeactivateJobs from "../hooks/useDeactivateJobs";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import { toast } from "../../../../components/toastNotification";
import { useQueryClient } from "react-query";
import { IReportsDetails } from "../types";
import ExecuteButton from "../../../../components/ExecuteButton";
import useExecuteJob from "../hooks/useExecuteJob";
import SignalRService from "services/SignalRService";

interface JobTableProps {}

const JobTable: React.FC<JobTableProps> = () => {
  const editPermission = PERMISSIONS.permission_utilities_jobconfiguration_edit;
  const addPermission = PERMISSIONS.permission_utilities_jobconfiguration_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_jobconfiguration_delete;
  const userPermissions = useRecoilValue(permissionsSelector);

  const [filterText, setFilterText] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [jobFilteredData, setJobFilteredData] = useState([]);
  const [actionAddState, setActionAddState] = useRecoilState(addShowState);
  const [showState, setShowState] = useRecoilState(jobShowState);
  const [actionEditState, setActionEditState] = useRecoilState(editState);
  const [selectedRowsToDelete, setSelectedRowsToDelete] = useRecoilState(
    selectedRowsJobToDelete
  );
  const [isFromRecent, setIsFromRecent] = useState(false);
  const [isResetFromRecent, setIsResetFromRecent] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [rowViewData, setRowViewData] = useState([]);
  const [, setRowToEdit] = useRecoilState(rowForEdit);
  const [jobId, setJobId] = useState("");
  const { mutateAsync: ExecuteJob } = useExecuteJob();
  const [, setExecutingRowId] = useState(null);

  const tableRef = useRef();
  let location = useLocation();
  const queryClient = useQueryClient();
  let recordIdFromRecent;

  const {
    data: jobData,
    isLoading: isJobsLoading,
    refetch: refetchJobs,
  } = useGetJobs();

  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  const [isProcessing, setIsProcessing] = useState<{ [key: string]: boolean }>(
    {}
  );
  useEffect(() => {
    const handleJobStatusUpdate = (jobId, isRunning) => {
      setIsProcessing((prevState) => ({
        ...prevState,
        [jobId]: isRunning,
      }));
    };

    SignalRService.onJobStatusUpdate(handleJobStatusUpdate);
    return () => {
      SignalRService.offJobStatusUpdate(handleJobStatusUpdate); //Unsubscribe
    };
  }, [SignalRService]);

  useEffect(() => {
    if (jobId !== "") {
      if (jobId?.includes(".")) {
        let ids = jobId?.split(".");
        const mappedIds = ids.map((s) => Number(s));
        const filteredData = jobData.filter((x) =>
          mappedIds.includes(x.jobConfigId)
        );
        setJobFilteredData(filteredData);
      } else {
        const filteredData = jobData?.filter(
          (x) => x.jobConfigId === Number(jobId)
        );
        setJobFilteredData(filteredData);
      }
    }
    if (isResetFromRecent) {
      setJobFilteredData([]);
      setIsFromRecent(false);
    }
  }, [jobId, jobData, isResetFromRecent]);

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setJobId(recordIdFromRecent.recordId);
          setJobFilteredData(result);
          setIsFromRecent(true);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [recordIdFromRecent]);

  const {
    mutate: BulkDeactivateJobs,
    isSuccess: deacSuccess,
    data: deacResult,
    reset: resetDeacData,
  } = useDeactivateJobs();

  useEffect(() => {
    setRowViewData([]);
    setShowState(false);
    setActionAddState(false);
    setActionEditState(false);
  }, []);

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRowsToDelete(forDelete);
  };
  const handleBulkDelete = () => {
    const ids = selectedRowsToDelete?.map((row) => row.jobConfigId);
    BulkDeactivateJobs(ids);
    setOpenModalDelete(false);
    setSelectedRowsToDelete([]);
  };
  useEffect(() => {
    if (filterText !== "") {
      const filteredItems = jobData?.filter((item) =>
        item.eventName
          ?.toLowerCase()
          ?.includes(filterText?.toLowerCase()?.trim())
      );
      setJobFilteredData(filteredItems);
    } else {
      setJobFilteredData([]);
    }
  }, [filterText, jobData]);

  const onClickEventName = (rowData) => {
    setRowViewData(rowData);
    setShowState(true);
  };
  const scheduleDaysOpts: any = [
    { value: "MON", label: "Monday" },
    { value: "TUE", label: "Tuesday" },
    { value: "WED", label: "Wednesday" },
    { value: "THU", label: "Thursday" },
    { value: "FRI", label: "Friday" },
    { value: "SAT", label: "Saturday" },
    { value: "SUN", label: "Sunday" },
  ];
  const monthDaysOpts = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
  }));
  const handleEditButton = (rowData) => {
    const schedDaysValue = rowData?.scheduleDays?.split(",");
    const filterSched = schedDaysValue?.map((z) =>
      scheduleDaysOpts?.filter((c) => c.value === z)
    );
    const schedDays = filterSched?.map((x) => x?.map((c) => c)[0]);
    const reports = [] as Array<IReportsDetails>;
    if (rowData?.configReports && rowData?.configReports?.length > 0) {
      rowData.configReports.forEach((obj: IReportsDetails, index) => {
        const reportsData = {
          ...obj,
          parentRow: index > 0 ? false : true,
          tableName: "configReports",
        };
        reports.push(reportsData);
      });
    }
    setRowToEdit({
      ...rowData,
      createdDate: rowData.createdDate
        ? rowData.createdDate?.split("T")[0]
        : "",
      modifiedDate: rowData.modifiedDate
        ? rowData.modifiedDate?.split("T")[0]
        : "",
      scheduleDays: rowData.scheduleDays ? schedDays : "",
      configReports: [...reports],
    });
    setActionEditState(!actionEditState);
  };
  if (deacSuccess && deacResult) {
    if (deacResult.status === 201 || deacResult.status === 200) {
      toast.fire("Success", "Jobs were deactivated successfully", "success");
      queryClient.invalidateQueries("GetJobs");
      setSelectedRowsToDelete(undefined);
    } else if (deacResult.status === 500) {
      toast.fire(
        "Warning",
        "Jobs were successfully deactivated, but " + deacResult.data.message,
        "warning"
      );
    } else {
      toast.fire(
        "Bulk deactivation of Jobs failed",
        "Server Error: Cannot deactivate Jobs",
        "error"
      );
      queryClient.invalidateQueries("GetJobs");
    }
    resetDeacData();
  }

  const handleExecute = (rowData) => {
    setExecutingRowId(rowData.jobConfigId);
    ExecuteJob(rowData, {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          queryClient.invalidateQueries(["GetJobs"]);
          setExecutingRowId(null);
          toast.fire("Success", "Job executed successfully", "success");
        } else {
          setExecutingRowId(null);
          toast.fire("Failed", "Failed to execute Job", "error");
        }
      },
      onError: (error) => {
        toast.fire("Failed", "Failed to execute Job", "error");
      },
    });
  };

  const alignCenter = "center" as
    | "center"
    | "inherit"
    | "justify"
    | "left"
    | "right"
    | undefined;

  const matColumns = [
    {
      title: "Event Name",
      field: "eventName",
      customSort: (a, b) =>
        a.eventName && b.eventName
          ? a.eventName.trim().localeCompare(b.eventName.trim())
          : a.eventName
          ? 0
          : -1,
      render: (rowData) => {
        return (
          <div
            className="cursor-pointer blueAndBold underline "
            onClick={() => onClickEventName(rowData)}
          >
            {rowData.eventName}
          </div>
        );
      },
    },

    {
      title: "Schedule Days",
      field: "scheduleDays",
    },
    {
      title: "Schedule Time",
      field: "scheduleTime",
    },
    {
      title: "Recurring",
      field: "recurring",
    },
    {
      title: "Subject",
      field: "subject",
    },
    {
      title: "Server Instance",
      field: "serverInstance",
    },
    {
      title: "With Email",
      field: "withEmail",
      render: (rowData) => {
        return <>{rowData.withEmail ? "Yes" : "No"}</>;
      },
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return (
          <div>
            <div className="font-sans text-left mr-4 text-xs">
              {rowData.isActive ? "Active" : "Inactive"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action/s",
      align: alignCenter,
      render: (rowData) => {
        const isExecuting = isProcessing[rowData.guid]; // Check if job is in processing state
        return userPermissions.includes(editPermission) ? (
          <div className="flex items-center place-content-center mr-5">
            <div className="flex gap-2 sm:flex-col xl:flex-row justify-items-center">
              <ExecuteButton
                disabled={isExecuting || rowData.isActive === false}
                buttonText={isExecuting ? "Executing..." : "Execute"}
                onClick={() => {
                  handleExecute(rowData);
                }}
              />
              <EditButton
                disabled={selectedRowsToDelete?.length ? true : false}
                className={"opacity-100 "}
                onClick={() => {
                  setActionEditState(true);
                  handleEditButton(rowData);
                }}
              />
            </div>
          </div>
        ) : (
          ""
        );
      },
    },
  ];
  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${
      selectedRowsToDelete?.length
    } ${
      selectedRowsToDelete
        ? selectedRowsToDelete?.length > 1
          ? "Jobs"
          : "Job"
        : ""
    }?`}</div>
  );
  return (
    <>
      <div className="animate-fade-in-down bg-white space-y-3 px-8 py-4">
        {actionAddState || showState || actionEditState ? (
          ""
        ) : (
          <>
            <div className="w-full">
              <div className="flex gap-2 items-center">
                <div>
                  <BriefcaseIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
                </div>
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs font-medium text-gray-500">
                    Utilities Module
                  </h3>
                  <h1 className="mr-auto font-bold text-primary text-md uppercase">
                    Job CONFIGURATION
                  </h1>
                </div>
              </div>
              <div className="h-0.5 w-full border-b-2 border-secondary mt-2 mb-4" />
              <div className="mt-4 rounded-md">
                <div className="flex justify-between text-xs items-center w-full">
                  <TableFilters
                    setIsSearching={setIsSearching}
                    setFilterText={setFilterText}
                    filterText={filterText}
                    moduleDetails="Event Name"
                    textInputName="eventName"
                    disabledButton={false}
                    isFromRecent={isFromRecent}
                    setIsResetFromRecent={setIsResetFromRecent}
                  />
                  {userPermissions.includes(addPermission) ? (
                    <AddButton
                      buttonText="New Event Name"
                      onClick={() => setActionAddState(true)}
                      disabled={actionEditState}
                    />
                  ) : (
                    ""
                  )}
                </div>
                <div>
                  {userPermissions.includes(deletePermission) ? (
                    <div className="text-secondary italic text-xs3 my-2">
                      <p className="font-bold italic inline-block mr-1">
                        Note:
                      </p>
                      <span>Tick the box to deactivate some data.</span>
                    </div>
                  ) : (
                    <div className="mb-4"></div>
                  )}
                </div>
              </div>
              <div className="pb-4">
                <MaterialTableTS
                  columns={matColumns}
                  data={isSearching ? jobFilteredData : jobData}
                  isLoading={isJobsLoading}
                  tableRef={tableRef}
                  selection={userPermissions.includes(deletePermission)}
                  onSelectionChange={handleSelectedRowsChange}
                  selectedRows={selectedRowsToDelete}
                  setConfirmModalOpen={setOpenModalDelete}
                />
              </div>
            </div>
          </>
        )}
        {actionAddState || actionEditState ? (
          <div className="">
            <JobForm
              scheduleDaysOpts={scheduleDaysOpts}
              refetchJobs={refetchJobs}
              monthDaysOpts={monthDaysOpts}
            />
          </div>
        ) : (
          ""
        )}
        {showState && (
          <div
            className={`${
              showState && (actionEditState || actionAddState) && "hidden"
            } relative px-2 text-xs py-2 box-shadow border-quinary border-l-2`}
          >
            <JobViewDetails
              rowData={rowViewData}
              handleEdit={handleEditButton}
              setActionAddState={setActionAddState}
            />
          </div>
        )}
      </div>
      <ConfirmBoxDelete
        ModalTitle="Deactivate Job"
        qConfirmation={deleteMessage}
        isOpen={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        onClickFunction={handleBulkDelete}
        cancelButtonRef={undefined}
      />
    </>
  );
};

export default JobTable;
